import React from 'react';

import { WarningOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import {
  IntegratedErps,
  PurchaseTargetStatus,
  RecurrencyReplenishmentMethod,
  RecurrencyRole,
  TargetGroupType,
  TenantProductName,
  TenantUserPermission,
} from '@recurrency/core-api-schema/dist/common/enums';
import { SearchPlanningDTO } from '@recurrency/core-api-schema/dist/search/getSearchPlanning';
import { SearchIndexName } from '@recurrency/core-api-schema/dist/searchIndex/common';
import { Col, Row } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { theme } from 'theme';

import { LinkButton } from 'components/Button/LinkButton';
import { Card, CardSection } from 'components/Card';
import { FlexSpace } from 'components/FlexSpace';
import { Typography } from 'components/Typography';

import { useCoreApi } from 'hooks/useApi';
import { useGlobalApp } from 'hooks/useGlobalApp';

import { formatNumber } from 'utils/formatting';
import { arePermissionsEnabled, hasUserPermission, isRecurrencyRole, shouldShowProduct } from 'utils/roleAndTenant';
import { routes } from 'utils/routes';
import { useSearchIndex } from 'utils/search/search';
import { SearchResponse } from 'utils/search/types';

import { InventoryBriefSection } from './InventoryBrief';
import { InventorySnapshotsChart } from './InventorySnapshotChart';

interface AlertRowProps {
  title: string;
  icon?: JSX.Element;
  description?: string;
  actionUrl?: string;
}

const AlertRow = ({ title, icon, description, actionUrl }: AlertRowProps) => (
  <div
    className={css`
      display: flex;
      justify-content: space-between;
      align-items: center;
    `}
  >
    <div>
      <FlexSpace direction="row">
        <Typography type="middle" style={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
        {icon}
      </FlexSpace>
      {description && (
        <Typography type="middle" style={{ fontSize: '14px' }}>
          {description}
        </Typography>
      )}
    </div>
    <div>
      {actionUrl && (
        <LinkButton route={actionUrl} type="primary">
          Review
        </LinkButton>
      )}
    </div>
  </div>
);

export function OverviewTab() {
  const { activeTenant } = useGlobalApp();
  const { md: isDesktop } = useBreakpoint();

  const shouldShowDemandPlanningProduct = shouldShowProduct(activeTenant, TenantProductName.DfpaDemandPlanning, true);
  const canSeeDesyncedItems = arePermissionsEnabled()
    ? hasUserPermission(activeTenant, TenantUserPermission.AccessSettingsMonitoringView)
    : isRecurrencyRole(activeTenant, RecurrencyRole.TenantAdmin);
  const shouldShowDesyncedItems = activeTenant.erpType !== IntegratedErps.NETSUITE && canSeeDesyncedItems;

  const canViewDailyWorkflow = hasUserPermission(activeTenant, TenantUserPermission.AccessPlanningDailyWorkflowView);
  const canViewPlanning = hasUserPermission(activeTenant, TenantUserPermission.AccessPlanningPlanningView);
  const canViewPurchaseTargets = hasUserPermission(
    activeTenant,
    TenantUserPermission.AccessPurchasingPurchaseTargetsView,
  );

  // Show PO alert if purchasing automation product is enabled
  const hasDfpaPurchasingAutomationProduct = shouldShowProduct(
    activeTenant,
    TenantProductName.DfpaPurchasingAutomation,
  );

  // PURCHASE TARGET ACTIONS
  const { data: purchaseTargetData, isLoading: purchaseTargetLoading } = useCoreApi(
    schemas.purchasing.getPurchaseTargets,
    hasDfpaPurchasingAutomationProduct && canViewPurchaseTargets
      ? {
          queryParams: {
            targetGroupType: TargetGroupType.Targets,
            filter: { status: PurchaseTargetStatus.Ready },
          },
        }
      : null,
  );

  // NEEDS UPDATE ACTIONS
  const { data: minMaxData, isLoading: minMaxIsLoading } = useSearchIndex<SearchPlanningDTO>(
    canViewPlanning
      ? {
          indexName: SearchIndexName.Planning,
          hitsPerPage: 1,
          fieldsToRetrieve: ['item_id', 'planning_status', 'recurrency_replenishment_method'],
          filters: {
            planning_status: ['Review'],
          },
          facetFields: ['planning_status', 'recurrency_replenishment_method'],
        }
      : null,
  );

  // DESYNCED ITEMS ACTIONS
  const { data: desyncedData, isLoading: desyncedIsLoading } = useCoreApi(
    schemas.purchasing.getDesyncedItemLocations,
    canSeeDesyncedItems ? {} : null,
  );

  const { data: onboardingData, isLoading: onboardingLoading } = useSearchIndex<SearchPlanningDTO>(
    canViewPlanning
      ? {
          indexName: SearchIndexName.Planning,
          hitsPerPage: 0,
          fieldsToRetrieve: [],
          facetFields: ['recurrency_replenishment_method', 'excluded_until_category'],
        }
      : null,
  );

  const { data: dailyWorkflowTasksData, isLoading: isDailyWorkflowTasksLoading } = useCoreApi(
    schemas.dailyWorkflow.getDailyWorkflowTasks,
    canViewDailyWorkflow ? {} : null,
  );

  const getMinMaxItemsToReviewCount = (searchResponse: SearchResponse<SearchPlanningDTO>) =>
    searchResponse.facets.recurrency_replenishment_method
      ?.filter(
        (element, _) =>
          element.value === RecurrencyReplenishmentMethod.MinMax ||
          element.value === RecurrencyReplenishmentMethod.Unsupported,
      )
      .reduce((total, facet) => total + facet.count || 0, 0) ?? 0;

  const opOqCount =
    minMaxData?.facets.recurrency_replenishment_method?.find(
      (element, _) => element.value === RecurrencyReplenishmentMethod.OPOQ,
    )?.count ?? 0;

  const expiringSoonCount =
    onboardingData?.facets.excluded_until_category?.find((element, _) => element.value === 'Next 7 Days')?.count ?? 0;

  const showExpiringSoonComponent = !!(
    canViewPlanning &&
    shouldShowDemandPlanningProduct &&
    !onboardingLoading &&
    onboardingData &&
    expiringSoonCount > 0
  );

  const alertsCard = (
    <Card style={{ minWidth: '435px' }}>
      {shouldShowDesyncedItems && !desyncedIsLoading && desyncedData && desyncedData.totalCount > 0 && (
        <CardSection>
          <AlertRow
            title={`${formatNumber(desyncedData.totalCount)} Items`}
            icon={<WarningOutlined style={{ color: theme.colors.warning[500] }} />}
            description="require resyncing with ERP"
            actionUrl={routes.monitoring.syncStatus()}
          />
        </CardSection>
      )}
      {canViewDailyWorkflow &&
        shouldShowDemandPlanningProduct &&
        !isDailyWorkflowTasksLoading &&
        dailyWorkflowTasksData && (
          <CardSection>
            <AlertRow
              title={`${formatNumber(
                dailyWorkflowTasksData.items.reduce(
                  (total, item) => total + (item.taskCount - item.reviewedItemLocations.length),
                  0,
                ),
              )} Items`}
              description="from daily workflow require review"
              actionUrl={routes.demandPlanning.dailyWorkflow()}
            />
          </CardSection>
        )}
      {canViewPurchaseTargets && !purchaseTargetLoading && purchaseTargetData && hasDfpaPurchasingAutomationProduct && (
        <CardSection>
          <AlertRow
            title={`${formatNumber(purchaseTargetData.totalCount)} Purchase Orders`}
            description="ready to purchase"
            actionUrl={routes.purchasing.purchaseTargets({ status: PurchaseTargetStatus.Ready })}
          />
        </CardSection>
      )}
      {canViewPlanning && !minMaxIsLoading && minMaxData && (
        <CardSection>
          <AlertRow
            title={`${formatNumber(opOqCount)} Items`}
            description="require updates to OP/OQ"
            actionUrl={routes.demandPlanning.planning({
              where: {
                planning_status: ['Review'],
                recurrency_replenishment_method: [RecurrencyReplenishmentMethod.OPOQ],
              },
            })}
          />
        </CardSection>
      )}
      {canViewPlanning && !minMaxIsLoading && minMaxData && (
        <CardSection>
          <AlertRow
            title={`${formatNumber(getMinMaxItemsToReviewCount(minMaxData))} Items`}
            description="require updates to Min/Max"
            actionUrl={routes.demandPlanning.planning({ where: { planning_status: ['Review'] } })}
          />
        </CardSection>
      )}
      {showExpiringSoonComponent && (
        <CardSection>
          <AlertRow
            title={`${formatNumber(expiringSoonCount)} Items`}
            description="have exclusions expiring within 7 days"
            actionUrl={routes.demandPlanning.planning({
              where: { excluded_until_category: ['Next 7 Days'] },
              sortBy: { field: 'excluded_until', order: 'asc' },
            })}
          />
        </CardSection>
      )}
    </Card>
  );

  return (
    <>
      <Row gutter={18} wrap={isDesktop ? false : undefined}>
        <Col
          flex={isDesktop ? `0 1 432px` : undefined}
          span={isDesktop ? undefined : 24}
          className={css`
            margin-bottom: 24px;
          `}
        >
          <Typography type="h3" style={{ fontWeight: 600, marginBottom: 12 }}>
            Inventory Alerts
          </Typography>
          {alertsCard}
        </Col>
        <Col
          flex={isDesktop ? `1 1 0%` : undefined}
          span={isDesktop ? undefined : 24}
          className={css`
            margin-bottom: 24px;
          `}
        >
          <InventorySnapshotsChart />
        </Col>
      </Row>
      <InventoryBriefSection />
    </>
  );
}
